import { Button } from "@mui/material";
import courseImage from "src/Common/CourseImage";

interface Courses {
    courseId: number;
    courseName: string;
    courseDescription: string;
    percentage: number;
    images: string;
    packageId: number;
    courseVideoCount: number;
    purchased: boolean;
    onServer: boolean;
    isSubscribed: boolean;
    startDate: string;
    endDate: string;
}

interface MyCoursePackagesProps {
    courses: Courses[];
    courseStartLearning: (course: Courses) => void;
}

const MyCoursesDisplay: React.FC<MyCoursePackagesProps> = ({
    courses,
    courseStartLearning,
}) => {

    // ============================================================
    // DATE PARSER
    // Handles both:
    // 2026-07-31
    // 2026-07-31T00:00:00
    // ============================================================
    const parseDate = (date: string) => {
        if (!date) return null;

        // Handle date-only values safely in local timezone
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const [year, month, day] = date
                .split("-")
                .map(Number);

            return new Date(
                year,
                month - 1,
                day
            );
        }

        const parsedDate = new Date(date);

        if (Number.isNaN(parsedDate.getTime())) {
            return null;
        }

        return parsedDate;
    };


    // ============================================================
    // FORMAT DATE
    // Example:
    // 31 Jul 2026
    // ============================================================
    const formatDate = (date: string) => {
        const parsedDate = parseDate(date);

        if (!parsedDate) {
            return date;
        }

        return parsedDate.toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        );
    };


    // ============================================================
    // GET REMAINING DAYS
    // ============================================================
    const getRemainingDays = (endDate: string) => {
        const parsedEndDate = parseDate(endDate);

        if (!parsedEndDate) {
            return null;
        }

        const today = new Date();

        today.setHours(
            0,
            0,
            0,
            0
        );

        parsedEndDate.setHours(
            0,
            0,
            0,
            0
        );

        const difference =
            parsedEndDate.getTime() -
            today.getTime();

        return Math.ceil(
            difference /
            (1000 * 60 * 60 * 24)
        );
    };


    return (
        <div
            style={{
                width: "100%",
                background: "transparent",
            }}
        >

            <div
                className="myCoursesPackage"
                style={{
                    display: "grid",
                    gridTemplateColumns:
                        "repeat(auto-fill, minmax(290px, 1fr))",
                    gap: "24px",
                    width: "100%",
                }}
            >

                {courses.map((course) => {

                    const poster =
                        courseImage.find(
                            data =>
                                data.courseId ===
                                course.courseId
                        )?.posterLink;


                    // ========================================================
                    // COURSE STATUS
                    // ========================================================

                    const progress = Math.min(
                        Math.max(
                            Number(course.percentage) || 0,
                            0
                        ),
                        100
                    );

                    const isStarted =
                        progress > 0;

                    const isCompleted =
                        progress >= 100;


                    // ========================================================
                    // SUBSCRIPTION
                    //
                    // Show ONLY when:
                    // isSubscribed === true
                    // AND startDate exists
                    // AND endDate exists
                    // ========================================================

                    const hasSubscription =
                        course.isSubscribed === true &&
                        !!course.startDate &&
                        !!course.endDate;


                    const remainingDays =
                        hasSubscription
                            ? getRemainingDays(
                                course.endDate
                            )
                            : null;


                    const subscriptionExpired =
                        hasSubscription &&
                        remainingDays !== null &&
                        remainingDays < 0;


                    return (

                        <div
                            key={course.courseId}
                            style={{
                                position: "relative",
                                width: "100%",
                                minWidth: 0,

                                background:
                                    "linear-gradient(145deg, rgba(255,255,255,0.075), rgba(255,255,255,0.025))",

                                border:
                                    "1px solid rgba(245,180,95,0.18)",

                                borderRadius:
                                    "20px",

                                overflow: "hidden",

                                boxShadow:
                                    "0 15px 40px rgba(0,0,0,0.35)",

                                backdropFilter:
                                    "blur(12px)",

                                transition:
                                    "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                            }}

                            onMouseEnter={(e) => {

                                e.currentTarget.style.transform =
                                    "translateY(-6px)";

                                e.currentTarget.style.boxShadow =
                                    "0 20px 45px rgba(245,180,95,0.14)";

                                e.currentTarget.style.borderColor =
                                    "rgba(245,180,95,0.45)";
                            }}

                            onMouseLeave={(e) => {

                                e.currentTarget.style.transform =
                                    "translateY(0)";

                                e.currentTarget.style.boxShadow =
                                    "0 15px 40px rgba(0,0,0,0.35)";

                                e.currentTarget.style.borderColor =
                                    "rgba(245,180,95,0.18)";
                            }}
                        >

                            {/* =================================================
                                FUTURISTIC TOP GLOW
                            ================================================== */}

                            <div
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: "15%",
                                    width: "70%",
                                    height: "2px",

                                    background:
                                        "linear-gradient(90deg, transparent, #f5b45f, transparent)",

                                    boxShadow:
                                        "0 0 15px rgba(245,180,95,0.8)",

                                    zIndex: 5,
                                }}
                            />


                            {/* =================================================
                                COURSE IMAGE
                            ================================================== */}

                            <div
                                style={{
                                    position: "relative",
                                    width: "100%",
                                    height: "190px",
                                    overflow: "hidden",
                                    background: "#080808",
                                }}
                            >

                                {poster ? (

                                    <img
                                        src={poster}
                                        alt={
                                            course.courseName ||
                                            course.images
                                        }
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",

                                            transition:
                                                "transform 0.5s ease",
                                        }}

                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform =
                                                "scale(1.06)";
                                        }}

                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform =
                                                "scale(1)";
                                        }}
                                    />

                                ) : (

                                    <div
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "#555",
                                            fontSize: "40px",
                                        }}
                                    >
                                        <i className="fa-solid fa-graduation-cap" />
                                    </div>

                                )}


                                {/* IMAGE OVERLAY */}

                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,

                                        background:
                                            "linear-gradient(to bottom, rgba(0,0,0,0.05) 30%, rgba(0,0,0,0.9) 100%)",
                                    }}
                                />


                                {/* COURSE STATUS */}

                                <div
                                    style={{
                                        position: "absolute",
                                        top: "14px",
                                        right: "14px",

                                        padding:
                                            "6px 11px",

                                        borderRadius:
                                            "20px",

                                        background:
                                            "rgba(0,0,0,0.65)",

                                        border:
                                            "1px solid rgba(245,180,95,0.35)",

                                        backdropFilter:
                                            "blur(8px)",

                                        color:
                                            "#f5b45f",

                                        fontSize:
                                            "11px",

                                        fontWeight:
                                            700,

                                        letterSpacing:
                                            "0.5px",
                                    }}
                                >

                                    <i
                                        className={
                                            isCompleted
                                                ? "fa-solid fa-circle-check"
                                                : "fa-solid fa-bolt"
                                        }

                                        style={{
                                            marginRight:
                                                "5px",
                                        }}
                                    />

                                    {isCompleted
                                        ? "COMPLETED"
                                        : isStarted
                                            ? "IN PROGRESS"
                                            : "READY TO START"}

                                </div>


                                {/* COURSE NUMBER */}

                                <div
                                    style={{
                                        position: "absolute",
                                        bottom: "12px",
                                        left: "15px",

                                        width: "34px",
                                        height: "34px",

                                        borderRadius: "50%",

                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",

                                        background:
                                            "rgba(0,0,0,0.65)",

                                        border:
                                            "1px solid rgba(255,255,255,0.18)",

                                        color: "#f5b45f",

                                        fontSize: "13px",
                                    }}
                                >
                                    <i className="fa-solid fa-play" />
                                </div>

                            </div>


                            {/* =================================================
                                COURSE CONTENT
                            ================================================== */}

                            <div
                                style={{
                                    padding:
                                        "18px 18px 20px",
                                }}
                            >

                                {/* COURSE NAME */}

                                <h3
                                    style={{
                                        margin:
                                            "0 0 8px",

                                        color:
                                            "#ffffff",

                                        fontSize:
                                            "18px",

                                        fontWeight:
                                            700,

                                        lineHeight:
                                            "1.3",

                                        letterSpacing:
                                            "0.2px",

                                        display:
                                            "-webkit-box",

                                        WebkitLineClamp:
                                            2,

                                        WebkitBoxOrient:
                                            "vertical",

                                        overflow:
                                            "hidden",
                                    }}
                                >
                                    {course.courseName}
                                </h3>


                                {/* DESCRIPTION */}

                                <p
                                    style={{
                                        margin:
                                            "0 0 18px",

                                        color:
                                            "#8f8f8f",

                                        fontSize:
                                            "13px",

                                        lineHeight:
                                            "1.6",

                                        minHeight:
                                            "42px",

                                        display:
                                            "-webkit-box",

                                        WebkitLineClamp:
                                            2,

                                        WebkitBoxOrient:
                                            "vertical",

                                        overflow:
                                            "hidden",
                                    }}
                                >
                                    {course.courseDescription}
                                </p>


                                {/* =================================================
                                    SUBSCRIPTION INFORMATION
                                    
                                    ONLY SHOWN WHEN:
                                    isSubscribed === true
                                    AND BOTH DATES EXIST
                                ================================================== */}

                                {hasSubscription && (

                                    <div
                                        style={{
                                            marginBottom:
                                                "16px",

                                            padding:
                                                "11px 12px",

                                            borderRadius:
                                                "12px",

                                            background:
                                                subscriptionExpired
                                                    ? "rgba(255,80,80,0.06)"
                                                    : "rgba(245,180,95,0.06)",

                                            border:
                                                subscriptionExpired
                                                    ? "1px solid rgba(255,80,80,0.20)"
                                                    : "1px solid rgba(245,180,95,0.18)",

                                            boxShadow:
                                                subscriptionExpired
                                                    ? "0 0 15px rgba(255,80,80,0.03)"
                                                    : "0 0 15px rgba(245,180,95,0.03)",
                                        }}
                                    >

                                        {/* SUBSCRIPTION HEADER */}

                                        <div
                                            style={{
                                                display:
                                                    "flex",

                                                alignItems:
                                                    "center",

                                                justifyContent:
                                                    "space-between",

                                                marginBottom:
                                                    "8px",
                                            }}
                                        >

                                            <div
                                                style={{
                                                    display:
                                                        "flex",

                                                    alignItems:
                                                        "center",

                                                    gap:
                                                        "7px",

                                                    color:
                                                        subscriptionExpired
                                                            ? "#ff7777"
                                                            : "#f5b45f",

                                                    fontSize:
                                                        "11px",

                                                    fontWeight:
                                                        800,

                                                    letterSpacing:
                                                        "0.4px",

                                                    textTransform:
                                                        "uppercase",
                                                }}
                                            >

                                                <i
                                                    className={
                                                        subscriptionExpired
                                                            ? "fa-solid fa-circle-xmark"
                                                            : "fa-solid fa-circle-check"
                                                    }
                                                />

                                                {subscriptionExpired
                                                    ? "Subscription Expired"
                                                    : "Active Subscription"}

                                            </div>

                                        </div>


                                        {/* END DATE */}

                                        <div
                                            style={{
                                                display:
                                                    "flex",

                                                alignItems:
                                                    "center",

                                                justifyContent:
                                                    "space-between",

                                                gap:
                                                    "10px",

                                                marginBottom:
                                                    "6px",
                                            }}
                                        >

                                            <div
                                                style={{
                                                    display:
                                                        "flex",

                                                    alignItems:
                                                        "center",

                                                    gap:
                                                        "7px",

                                                    color:
                                                        "#888",

                                                    fontSize:
                                                        "11px",
                                                }}
                                            >

                                                <i
                                                    className="fa-regular fa-calendar"
                                                    style={{
                                                        color:
                                                            "#f5b45f",
                                                    }}
                                                />

                                                Ends On

                                            </div>


                                            <span
                                                style={{
                                                    color:
                                                        "#ffffff",

                                                    fontSize:
                                                        "11px",

                                                    fontWeight:
                                                        700,
                                                }}
                                            >
                                                {formatDate(
                                                    course.endDate
                                                )}
                                            </span>

                                        </div>


                                        {/* REMAINING DAYS */}

                                        {!subscriptionExpired &&
                                            remainingDays !== null && (

                                                <div
                                                    style={{
                                                        display:
                                                            "flex",

                                                        alignItems:
                                                            "center",

                                                        justifyContent:
                                                            "space-between",

                                                        gap:
                                                            "10px",
                                                    }}
                                                >

                                                    <div
                                                        style={{
                                                            display:
                                                                "flex",

                                                            alignItems:
                                                                "center",

                                                            gap:
                                                                "7px",

                                                            color:
                                                                "#888",

                                                            fontSize:
                                                                "11px",
                                                        }}
                                                    >

                                                        <i
                                                            className="fa-regular fa-clock"
                                                            style={{
                                                                color:
                                                                    "#f5b45f",
                                                            }}
                                                        />

                                                        Remaining

                                                    </div>


                                                    <span
                                                        style={{
                                                            color:
                                                                "#f5b45f",

                                                            fontSize:
                                                                "11px",

                                                            fontWeight:
                                                                800,
                                                        }}
                                                    >

                                                        {remainingDays === 0
                                                            ? "Expires Today"
                                                            : `${remainingDays} ${remainingDays === 1
                                                                ? "Day"
                                                                : "Days"
                                                            } Remaining`}

                                                    </span>

                                                </div>

                                            )}


                                        {/* EXPIRED DATE */}

                                        {subscriptionExpired && (

                                            <div
                                                style={{
                                                    color:
                                                        "#ff7777",

                                                    fontSize:
                                                        "10px",

                                                    marginTop:
                                                        "4px",

                                                    textAlign:
                                                        "right",
                                                }}
                                            >
                                                Your subscription period
                                                has ended.
                                            </div>

                                        )}

                                    </div>

                                )}


                                {/* =================================================
                                    COURSE INFO
                                ================================================== */}

                                <div
                                    style={{
                                        display:
                                            "flex",

                                        justifyContent:
                                            "space-between",

                                        alignItems:
                                            "center",

                                        marginBottom:
                                            "16px",

                                        padding:
                                            "9px 11px",

                                        borderRadius:
                                            "10px",

                                        background:
                                            "rgba(255,255,255,0.035)",

                                        border:
                                            "1px solid rgba(255,255,255,0.06)",
                                    }}
                                >

                                    <div
                                        style={{
                                            display:
                                                "flex",

                                            alignItems:
                                                "center",

                                            gap:
                                                "7px",

                                            color:
                                                "#888",

                                            fontSize:
                                                "11px",
                                        }}
                                    >

                                        <i
                                            className="fa-solid fa-video"
                                            style={{
                                                color:
                                                    "#f5b45f",
                                            }}
                                        />

                                        {course.courseVideoCount} Videos

                                    </div>


                                    <div
                                        style={{
                                            display:
                                                "flex",

                                            alignItems:
                                                "center",

                                            gap:
                                                "7px",

                                            color:
                                                "#888",

                                            fontSize:
                                                "11px",
                                        }}
                                    >

                                        <i
                                            className="fa-solid fa-shield-halved"
                                            style={{
                                                color:
                                                    "#f5b45f",
                                            }}
                                        />

                                        Enrolled

                                    </div>

                                </div>


                                {/* =================================================
                                    ACTION BUTTON
                                ================================================== */}

                                {course.courseVideoCount > 0 ? (

                                    <Button
                                        onClick={() =>
                                            courseStartLearning(
                                                course
                                            )
                                        }

                                        fullWidth

                                        style={{
                                            height:
                                                "45px",

                                            borderRadius:
                                                "12px",

                                            color:
                                                "#080808",

                                            background:
                                                "linear-gradient(135deg, #f5b45f, #ffd68e)",

                                            fontWeight:
                                                800,

                                            fontSize:
                                                "14px",

                                            textTransform:
                                                "none",

                                            letterSpacing:
                                                "0.2px",

                                            boxShadow:
                                                "0 7px 20px rgba(245,180,95,0.18)",

                                            transition:
                                                "all 0.25s ease",
                                        }}

                                        onMouseEnter={(e) => {

                                            e.currentTarget.style.transform =
                                                "translateY(-2px)";

                                            e.currentTarget.style.boxShadow =
                                                "0 10px 25px rgba(245,180,95,0.3)";
                                        }}

                                        onMouseLeave={(e) => {

                                            e.currentTarget.style.transform =
                                                "translateY(0)";

                                            e.currentTarget.style.boxShadow =
                                                "0 7px 20px rgba(245,180,95,0.18)";
                                        }}
                                    >

                                        <i
                                            className={
                                                isStarted
                                                    ? "fa-solid fa-play"
                                                    : "fa-solid fa-rocket"
                                            }

                                            style={{
                                                marginRight:
                                                    "9px",
                                            }}
                                        />

                                        {isStarted
                                            ? "Resume Learning"
                                            : "Start Learning"}

                                        <i
                                            className="fa-solid fa-arrow-right"
                                            style={{
                                                marginLeft:
                                                    "auto",
                                            }}
                                        />

                                    </Button>

                                ) : (

                                    <Button
                                        disabled
                                        fullWidth

                                        style={{
                                            height:
                                                "45px",

                                            borderRadius:
                                                "12px",

                                            color:
                                                "#777",

                                            background:
                                                "#1c1c1c",

                                            border:
                                                "1px solid #292929",

                                            fontWeight:
                                                700,

                                            fontSize:
                                                "14px",

                                            textTransform:
                                                "none",
                                        }}
                                    >

                                        <i
                                            className="fa-solid fa-clock"
                                            style={{
                                                marginRight:
                                                    "8px",
                                            }}
                                        />

                                        Coming Soon

                                    </Button>

                                )}

                            </div>

                        </div>
                    );
                })}

            </div>

        </div>
    );
};

export default MyCoursesDisplay;